import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { useContentProtection } from '../../hooks/useContentProtection';
import { 
  Award,
  Download,
  CheckCircle2,
  Clock,
  Target,
  ArrowLeft,
  ExternalLink,
  Shield,
  Calendar,
  User,
  BookOpen,
  Keyboard
} from 'lucide-react';
import DOMPurify from 'dompurify';

const RPATCertificate = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  const certificateRef = useRef(null);

  const fetchEligibility = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/certificates/eligibility');
      setEligibility(res.data);
      
      if (res.data.already_certified) {
        const certRes = await api.get('/rpat/certificates/my-certificate');
        setCertificate(certRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch eligibility:', err);
      toast.error('Failed to load certificate status');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchEligibility();
  }, [fetchEligibility]);

  const generateCertificate = async () => {
    try {
      setGenerating(true);
      const res = await api.post('/rpat/certificates/generate');
      setCertificate(res.data.certificate);
      setEligibility(prev => ({ ...prev, already_certified: true }));
      toast.success('Certificate generated successfully!');
    } catch (err) {
      console.error('Failed to generate certificate:', err);
      toast.error(err.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCertificate = () => {
    if (!certificate) return;
    
    // Create certificate HTML content
    const certificateHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>TekWissen Certified Recruiter - ${DOMPurify.sanitize(certificate.user_name)}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Inter:wght@400;500;600&display=swap');
          
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          body {
            font-family: 'Inter', sans-serif;
            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }
          
          .certificate {
            width: 900px;
            background: white;
            border: 3px solid #0891b2;
            border-radius: 8px;
            padding: 60px;
            position: relative;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          }
          
          .certificate::before {
            content: '';
            position: absolute;
            inset: 10px;
            border: 1px solid #e5e7eb;
            border-radius: 4px;
            pointer-events: none;
          }
          
          .header {
            text-align: center;
            margin-bottom: 40px;
          }
          
          .logo {
            font-size: 28px;
            font-weight: 700;
            color: #0891b2;
            letter-spacing: 2px;
            margin-bottom: 10px;
          }
          
          .title {
            font-family: 'Playfair Display', serif;
            font-size: 42px;
            color: #1e293b;
            margin-bottom: 10px;
          }
          
          .subtitle {
            font-size: 16px;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 3px;
          }
          
          .body {
            text-align: center;
            margin-bottom: 40px;
          }
          
          .presented {
            font-size: 14px;
            color: #64748b;
            margin-bottom: 15px;
          }
          
          .name {
            font-family: 'Playfair Display', serif;
            font-size: 36px;
            color: #0891b2;
            border-bottom: 2px solid #0891b2;
            display: inline-block;
            padding-bottom: 5px;
            margin-bottom: 25px;
          }
          
          .description {
            font-size: 16px;
            color: #475569;
            line-height: 1.8;
            max-width: 600px;
            margin: 0 auto;
          }
          
          .stats {
            display: flex;
            justify-content: center;
            gap: 40px;
            margin: 40px 0;
            padding: 20px;
            background: #f8fafc;
            border-radius: 8px;
          }
          
          .stat {
            text-align: center;
          }
          
          .stat-value {
            font-size: 24px;
            font-weight: 700;
            color: #0891b2;
          }
          
          .stat-label {
            font-size: 12px;
            color: #64748b;
            text-transform: uppercase;
          }
          
          .footer {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            margin-top: 40px;
            padding-top: 30px;
            border-top: 1px solid #e5e7eb;
          }
          
          .credential {
            text-align: left;
          }
          
          .credential-id {
            font-family: monospace;
            font-size: 14px;
            color: #0891b2;
            font-weight: 600;
          }
          
          .credential-label {
            font-size: 11px;
            color: #94a3b8;
            text-transform: uppercase;
          }
          
          .date {
            text-align: center;
          }
          
          .signature {
            text-align: right;
          }
          
          .signature-line {
            width: 150px;
            border-bottom: 1px solid #1e293b;
            margin-bottom: 5px;
            margin-left: auto;
          }
          
          .signature-name {
            font-size: 14px;
            color: #1e293b;
          }
          
          .signature-title {
            font-size: 11px;
            color: #64748b;
          }
          
          @media print {
            body { background: white; padding: 0; }
            .certificate { box-shadow: none; border: 2px solid #0891b2; }
          }
        </style>
      </head>
      <body>
        <div class="certificate">
          <div class="header">
            <div class="logo">TEKWISSEN</div>
            <h1 class="title">Certificate of Completion</h1>
            <div class="subtitle">Certified Recruiter Program</div>
          </div>
          
          <div class="body">
            <p class="presented">This is to certify that</p>
            <div class="name">${DOMPurify.sanitize(certificate.user_name)}</div>
            <p class="description">
              has successfully completed the <strong>Recruitment Process Autonomous Training (R-PAT)</strong> 
              program, demonstrating comprehensive knowledge and skills in professional recruitment practices, 
              including sourcing, candidate engagement, interview coordination, negotiation, and client management.
            </p>
          </div>
          
          <div class="stats">
            <div class="stat">
              <div class="stat-value">${DOMPurify.sanitize(String(certificate.topics_completed))}</div>
              <div class="stat-label">Topics Completed</div>
            </div>
            <div class="stat">
              <div class="stat-value">${DOMPurify.sanitize(String(certificate.final_exam_score))}%</div>
              <div class="stat-label">Final Exam Score</div>
            </div>
            <div class="stat">
              <div class="stat-value">${DOMPurify.sanitize(String(certificate.typing_tests_passed))}</div>
              <div class="stat-label">Typing Tests Passed</div>
            </div>
            <div class="stat">
              <div class="stat-value">${DOMPurify.sanitize(String(certificate.total_hours_completed))}h</div>
              <div class="stat-label">Hours Completed</div>
            </div>
          </div>
          
          <div class="footer">
            <div class="credential">
              <div class="credential-id">${DOMPurify.sanitize(certificate.credential_id)}</div>
              <div class="credential-label">Credential ID</div>
            </div>
            
            <div class="date">
              <div style="font-size: 14px; color: #1e293b;">
                ${new Date(certificate.issued_date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div style="font-size: 11px; color: #64748b;">Date Issued</div>
            </div>
            
            <div class="signature">
              <div class="signature-line"></div>
              <div class="signature-name">TekWissen Training</div>
              <div class="signature-title">Director of Learning</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Create a Blob and open in new window for printing
    const blob = new Blob([certificateHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    
    // Clean up the URL after the window loads
    if (printWindow) {
      printWindow.onload = () => {
        URL.revokeObjectURL(url);
        printWindow.print();
      };
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-96" />
        </div>
      </MainLayout>
    );
  }

  // Certificate View
  if (certificate) {
    return (
      <MainLayout>
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Your Certificate</h1>
              <p className="text-muted-foreground">TekWissen Certified Recruiter - Phase 1-4 (Foundation)</p>
            </div>
          </div>

          {/* Phase 1-4 Certificate Info Banner */}
          <div className="p-4 rounded-lg bg-cyan-50 dark:bg-cyan-950/30 border border-cyan-200 dark:border-cyan-800">
            <div className="flex items-start gap-3">
              <Award className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-cyan-800 dark:text-cyan-300">R-PAT Foundation Certificate (Phase 1-4)</p>
                <p className="text-sm text-cyan-700 dark:text-cyan-400 mt-1">
                  This certificate recognizes completion of the R-PAT foundation training. 
                  For the full TekWissen Certified Recruiter credential with specialization, 
                  complete Phases 5-8 in the Advanced Certification track.
                </p>
              </div>
            </div>
          </div>

          {/* Certificate Preview Card */}
          <Card className="overflow-hidden border-2 border-cyan-500">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-8 text-white text-center">
              <Award className="h-16 w-16 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-2">Congratulations!</h2>
              <p className="text-lg opacity-90">You have completed R-PAT Foundation Training</p>
              <Badge className="mt-2 bg-white/20 text-white border-0">Phase 1-4</Badge>
            </div>
            
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <p className="text-sm text-muted-foreground mb-2">Awarded to</p>
                <h3 className="text-2xl font-bold text-cyan-600">{certificate.user_name}</h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <BookOpen className="h-6 w-6 mx-auto text-cyan-600 mb-2" />
                  <p className="text-2xl font-bold">{certificate.topics_completed}</p>
                  <p className="text-xs text-muted-foreground">Topics</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Target className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                  <p className="text-2xl font-bold">{certificate.final_exam_score}%</p>
                  <p className="text-xs text-muted-foreground">Exam Score</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Keyboard className="h-6 w-6 mx-auto text-violet-600 mb-2" />
                  <p className="text-2xl font-bold">{certificate.typing_tests_passed}</p>
                  <p className="text-xs text-muted-foreground">Typing Tests</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <Clock className="h-6 w-6 mx-auto text-amber-600 mb-2" />
                  <p className="text-2xl font-bold">{certificate.total_hours_completed}h</p>
                  <p className="text-xs text-muted-foreground">Hours</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2 mb-4 sm:mb-0">
                  <Shield className="h-5 w-5 text-cyan-600" />
                  <div>
                    <p className="text-xs text-muted-foreground">Credential ID</p>
                    <p className="font-mono font-bold">{certificate.credential_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Issued</p>
                    <p className="font-medium">
                      {new Date(certificate.issued_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-center mt-6">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-cyan-600 to-blue-600"
                  onClick={downloadCertificate}
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download Certificate
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Verification Link */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <ExternalLink className="h-6 w-6 text-cyan-600" />
                <div className="flex-1">
                  <p className="font-medium">Share Your Achievement</p>
                  <p className="text-sm text-muted-foreground">
                    Anyone can verify your certificate using the credential ID
                  </p>
                </div>
                <Button 
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/rpat/verify/${certificate.credential_id}`);
                    toast.success('Verification link copied!');
                  }}
                >
                  Copy Link
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  // Eligibility View
  return (
    <MainLayout>
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/rpat')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Foundation Certification</h1>
            <p className="text-muted-foreground">R-PAT Phase 1-4 Certificate</p>
          </div>
        </div>

        {/* Certificate Type Clarification */}
        <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Award className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-300">Two Certificate Levels Available</p>
              <div className="text-sm text-blue-700 dark:text-blue-400 mt-2 space-y-2">
                <p><strong>1. Foundation (Phase 1-4):</strong> Complete R-PAT core training - You&apos;re here now</p>
                <p><strong>2. Advanced (Phase 5-8):</strong> Complete Job Mastery, Sourcing Labs, Role Play &amp; Final Assessment for the full TekWissen Certified Recruiter credential with specialization</p>
              </div>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader className="text-center">
            <Award className="h-16 w-16 mx-auto text-cyan-600 mb-4" />
            <CardTitle className="text-2xl">Foundation Certificate Requirements</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">Phase 1-4 Completion</p>
          </CardHeader>
          <CardContent>
            {eligibility?.eligible ? (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full mb-6">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">You meet all requirements!</span>
                </div>
                
                <p className="text-muted-foreground mb-6">
                  Congratulations! You have completed all topics, passed the final exam with 85%+, 
                  and passed all typing tests. Click below to generate your <strong>Foundation Certificate</strong>.
                </p>
                
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-cyan-600 to-blue-600"
                  onClick={generateCertificate}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate Foundation Certificate'}
                </Button>
                
                <p className="text-xs text-muted-foreground mt-4">
                  Want the full certification? Continue to Phase 5-8 for Advanced Certification.
                </p>
              </div>
            ) : (
              <div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-800 font-medium">
                    {eligibility?.reason || 'Complete all requirements to earn your certificate'}
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <BookOpen className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">Complete all 23 topics</p>
                      <p className="text-sm text-muted-foreground">All four phases required</p>
                    </div>
                    <Badge variant="secondary">Required</Badge>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Target className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">Pass final certification exam</p>
                      <p className="text-sm text-muted-foreground">Score 85% or higher</p>
                    </div>
                    <Badge variant="secondary">Required</Badge>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Keyboard className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">Pass all 10 typing tests</p>
                      <p className="text-sm text-muted-foreground">Meet WPM and accuracy requirements</p>
                    </div>
                    <Badge variant="secondary">Required</Badge>
                  </div>
                </div>
                
                <div className="mt-6 text-center">
                  <Button variant="outline" onClick={() => navigate('/rpat')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Return to Training
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default RPATCertificate;
